import { X } from "lucide-react";

// 12-hour time picker. Stores value as 24-hour "HH:MM" string for backend compat.
// Empty string = no time selected.

function parse24(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h24 = Math.max(0, Math.min(23, parseInt(m[1], 10)));
  const min = Math.max(0, Math.min(59, parseInt(m[2], 10)));
  return { h24, min };
}

function format24(h12, min, period) {
  let h24 = h12 % 12;
  if (period === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // every 5 minutes

export default function TimePicker({ value, onChange, className = "", testId }) {
  const parsed = parse24(value);
  const hour12 = parsed ? (parsed.h24 % 12 || 12) : "";
  const minute = parsed ? parsed.min : "";
  const period = parsed ? (parsed.h24 >= 12 ? "PM" : "AM") : "AM";

  const update = (h, m, p) => {
    if (h === "" || m === "") {
      onChange("");
      return;
    }
    onChange(format24(Number(h), Number(m), p));
  };

  const baseSel = "px-2 py-2 border border-[hsl(var(--brand-border))] rounded-md bg-white text-sm";

  return (
    <div className={`flex items-center gap-1.5 ${className}`} data-testid={testId}>
      <select
        value={hour12}
        onChange={(e) => update(e.target.value, minute === "" ? 0 : minute, period)}
        className={baseSel}
        aria-label="Hour"
      >
        <option value="">—</option>
        {HOURS.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-[hsl(var(--brand-muted))]">:</span>
      <select
        value={minute}
        onChange={(e) => update(hour12 === "" ? 12 : hour12, e.target.value, period)}
        className={baseSel}
        aria-label="Minute"
      >
        <option value="">—</option>
        {MINUTES.map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
        ))}
      </select>
      <select
        value={period}
        onChange={(e) => update(hour12 === "" ? 12 : hour12, minute === "" ? 0 : minute, e.target.value)}
        className={baseSel}
        aria-label="AM or PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          className="ml-1 p-1.5 rounded-md hover:bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))]"
          aria-label="Clear time"
          title="Clear time"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );
}
